import React, { useEffect, useState } from "react";
import { api, unwrapArray } from "@/lib/api";
import { SearchSelect } from "@/components/ui/search-select";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import type { ComplaintRecord, DispatchRecord, MasterLookupRecord } from "@/types";

const field = "w-full h-10 rounded-lg border border-input bg-background px-3 text-sm focus:outline-hidden focus:ring-2 focus:ring-ring";

interface Props {
  /** "new" to log a complaint, a complaint to correct it, null when closed */
  target: ComplaintRecord | "new" | null;
  /** Fixes the order (and offers its dispatches) when raised from an order */
  order?: { id: string; order_number: string; account_name?: string; dispatches?: DispatchRecord[] };
  onClose: () => void;
  onSaved: () => void;
}

/** Log a buyer's complaint against an order (and consignment), or just the buyer; correct an open one */
export function ComplaintFormDialog({ target, order, onClose, onSaved }: Props) {
  const { success, error } = useToast();
  const open = !!target;
  const editing = target && target !== "new" ? target : null;
  const [types, setTypes] = useState<MasterLookupRecord[]>([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ order_id: "", order_label: "", account_id: "", account_label: "", dispatch_id: "", complaint_type: "", description: "", claim_amount: "", raised_on: "", lot_number: "", affected_rolls: "" });
  const [dispatches, setDispatches] = useState<DispatchRecord[]>([]);

  useEffect(() => {
    if (!open) return;
    api.get("/api/MasterLookup/ByCategory/COMPLAINT_TYPE").then((r) => setTypes(unwrapArray<MasterLookupRecord>(r))).catch(() => undefined);
  }, [open]);

  useEffect(() => {
    if (!target) return;
    if (target === "new") {
      setForm({ order_id: order?.id || "", order_label: order?.order_number || "", account_id: "", account_label: "", dispatch_id: "", complaint_type: "", description: "", claim_amount: "", raised_on: new Date().toISOString().slice(0, 10), lot_number: "", affected_rolls: "" });
      setDispatches(order?.dispatches?.filter((d) => d.dispatch_status !== "CANCELLED") || []);
      return;
    }
    setForm({
      order_id: target.order_id || "", order_label: target.order?.order_number || "", account_id: target.account_id, account_label: target.account?.name || "",
      dispatch_id: target.dispatch_id || "", complaint_type: target.complaint_type, description: target.description,
      claim_amount: target.claim_amount != null ? String(Number(target.claim_amount)) : "", raised_on: String(target.raised_on).slice(0, 10),
      lot_number: target.lot_number || "", affected_rolls: target.affected_rolls || "",
    });
  }, [target, order?.id]);

  // Consignments of a chosen order, to point the complaint at one
  const loadDispatches = (orderId: string) => {
    setDispatches([]);
    if (!orderId) return;
    api
      .get(`/api/SalesOrder/ById/${orderId}`)
      .then((r: any) => setDispatches(((r?.dispatches || r?.data?.dispatches || []) as DispatchRecord[]).filter((d) => d.dispatch_status !== "CANCELLED")))
      .catch(() => setDispatches([]));
  };

  // Lots recorded on the chosen consignment (or on all of the order's consignments), offered as suggestions
  const lotOptions = [
    ...new Set(
      dispatches
        .filter((d) => !form.dispatch_id || d.id === form.dispatch_id)
        .flatMap((d) => (d.lines || []).flatMap((l) => [l.lot_number, ...(l.rolls || []).map((r) => r.lot_number)]))
        .filter(Boolean) as string[],
    ),
  ];

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.complaint_type) return error("Validation Error", "Choose the complaint type");
    if (!form.description.trim()) return error("Validation Error", "Describe the complaint");
    if (!editing && !form.order_id && !form.account_id) return error("Validation Error", "Choose the order or the buyer");
    const payload = {
      order_id: editing ? undefined : form.order_id || undefined,
      account_id: editing || form.order_id ? undefined : form.account_id || undefined,
      dispatch_id: editing ? undefined : form.order_id && form.dispatch_id ? form.dispatch_id : undefined,
      complaint_type: form.complaint_type,
      description: form.description.trim(),
      claim_amount: form.claim_amount === "" ? null : Number(form.claim_amount),
      raised_on: editing ? undefined : form.raised_on || undefined,
      lot_number: form.lot_number.trim() || null,
      affected_rolls: form.affected_rolls.trim() || null,
    };
    setSaving(true);
    try {
      if (editing) await api.put(`/api/Complaint/Update/${editing.id}`, payload);
      else await api.post("/api/Complaint/Insert", payload);
      success(editing ? "Complaint updated" : "Complaint logged", "");
      onSaved();
      onClose();
    } catch (err: any) {
      error("Could not save", err.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-xl md:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit complaint ${editing.complaint_number}` : "Log a complaint"}</DialogTitle>
          <DialogDescription>Record what the buyer reported, then investigate and resolve it from the Complaints list.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 py-2 text-sm">
          {!editing && !order && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-1.5">
                <Label>Order</Label>
                <SearchSelect
                  endpoint="/api/SalesOrder/List"
                  value={form.order_id}
                  selectedLabel={form.order_label}
                  getLabel={(row) => `${row.order_number} · ${row.account?.name || ""}`}
                  placeholder="Search orders"
                  allowEmpty
                  emptyLabel="-- Not about one order --"
                  onChange={(id, row) => {
                    setForm({ ...form, order_id: id, order_label: row?.order_number || "", dispatch_id: "", account_id: "" });
                    loadDispatches(id);
                  }}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Buyer {!form.order_id && "*"}</Label>
                {form.order_id ? (
                  <p className="h-10 flex items-center text-xs text-muted-foreground">Taken from the order</p>
                ) : (
                  <SearchSelect
                    endpoint="/api/Account/List"
                    value={form.account_id}
                    selectedLabel={form.account_label}
                    getLabel={(row) => row.name}
                    placeholder="Search buyers"
                    onChange={(id, row) => setForm({ ...form, account_id: id, account_label: row?.name || "" })}
                  />
                )}
              </div>
            </div>
          )}
          {!editing && order && <p className="text-xs text-muted-foreground">About order <strong className="text-foreground">{order.order_number}</strong>{order.account_name ? ` · ${order.account_name}` : ""}</p>}

          {!editing && form.order_id && dispatches.length > 0 && (
            <div className="space-y-1.5">
              <Label>Consignment</Label>
              <select value={form.dispatch_id} onChange={(e) => setForm({ ...form, dispatch_id: e.target.value })} className={field}>
                <option value="">-- The order in general --</option>
                {dispatches.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.dispatch_number} · {d.transporter}
                    {d.lr_number ? ` · LR ${d.lr_number}` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Complaint type *</Label>
              <select value={form.complaint_type} onChange={(e) => setForm({ ...form, complaint_type: e.target.value })} className={field}>
                <option value="">-- Choose --</option>
                {types.map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Claim amount (₹)</Label>
              <Input type="number" min={0} step="any" value={form.claim_amount} onChange={(e) => setForm({ ...form, claim_amount: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-1.5">
              <Label>Lot / batch</Label>
              <Input list="complaint-lots" value={form.lot_number} onChange={(e) => setForm({ ...form, lot_number: e.target.value })} placeholder={lotOptions.length ? "Choose or type" : "As printed on the roll"} />
              <datalist id="complaint-lots">
                {lotOptions.map((x) => (
                  <option key={x} value={x} />
                ))}
              </datalist>
            </div>
            <div className="space-y-1.5">
              <Label>Rolls affected</Label>
              <Input value={form.affected_rolls} onChange={(e) => setForm({ ...form, affected_rolls: e.target.value })} placeholder="e.g. R3, R7-R9" maxLength={500} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>What the buyer reported *</Label>
            <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Roll 3 is a shade lighter than the approved sample. Photos sent on WhatsApp." />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? <Loader2 className="size-4 animate-spin mr-1.5" /> : null}
              {editing ? "Save changes" : "Log complaint"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
